import React, { useEffect, useState } from "react";
import {
  API,
  DATASETS,
  addressFor,
  comparisonLabel,
  dateLabel,
  fetchJSON,
  money,
  numeric,
  present,
  priceDifference,
} from "./propertyData";

export default function PropertyPreview({
  feature,
  position,
  map,
  mode,
  cachedData,
  onLoaded,
  onEnter,
  onLeave,
  onDismiss,
  onOpen,
}) {
  const [state, setState] = useState(() =>
    cachedData ? { data: cachedData } : { loading: true }
  );
  useEffect(() => {
    if (cachedData) {
      setState({ data: cachedData });
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const id = feature.properties._id;
      if (!present(id)) {
        setState({ error: true });
        return;
      }
      fetchJSON(
        `${API}/api/${DATASETS[mode].detail}/detail?${new URLSearchParams({
          objId: id,
        })}`,
        controller.signal
      )
        .then((data) => {
          if (controller.signal.aborted) return;
          if (
            !data ||
            typeof data !== "object" ||
            Array.isArray(data) ||
            !present(data._id)
          )
            throw new Error("Invalid property response");
          setState({ data });
          onLoaded(id, data);
        })
        .catch(() => {
          if (!controller.signal.aborted) setState({ error: true });
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [feature, mode, cachedData, onLoaded]);
  useEffect(() => {
    const escape = (event) => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", escape);
    map.on("movestart", onDismiss);
    return () => {
      document.removeEventListener("keydown", escape);
      map.off("movestart", onDismiss);
    };
  }, [map, onDismiss]);
  const data = state.data;
  const sale = mode === "PPRPrice" || mode === "matchedWithPPR";
  const date = data
    ? dateLabel(sale ? data.ppr_sale_date : data.published_date)
    : null;
  const price = data
    ? sale
      ? data.ppr_price
      : data.price
    : feature.properties.price;
  const difference = data
    ? priceDifference(data.price, data.ppr_price)
    : feature.properties.under_pc;
  const point = map.latLngToContainerPoint(position),
    size = map.getSize();
  const width = Math.min(270, size.x - 24);
  const left = Math.max(12, Math.min(point.x + 20, size.x - width - 12));
  const top = Math.max(12, Math.min(point.y - 60, size.y - 290));
  return (
    <section
      className="property-preview"
      aria-label="Property preview"
      style={{ left, top, width, maxHeight: Math.max(80, size.y - top - 12) }}
      onMouseOver={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onLeave();
      }}
    >
      <span className="eyebrow">
        {sale ? "Sale price" : DATASETS[mode].price}
      </span>
      <strong className="preview-price">{money(price)}</strong>
      {data && <p className="address">{addressFor(data)}</p>}
      {mode === "matchedWithPPR" && (
        <p className="preview-comparison">{comparisonLabel(difference)}</p>
      )}
      {data && (
        <>
          <p>
            {sale ? "Sold" : "Listed"}: {date || "Date unavailable"}
          </p>
          <p className="muted">
            {[
              numeric(data.beds) !== null ? `${data.beds} beds` : null,
              present(data.property_type)
                ? String(data.property_type).replace(/_/g, " ")
                : null,
              present(data.ber)
                ? `BER ${String(data.ber).toUpperCase()}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </>
      )}
      {state.loading && (
        <p className="muted" role="status">
          Loading address and date…
        </p>
      )}
      {state.error && (
        <p className="muted">
          More details couldn’t be loaded. Open the property to try again.
        </p>
      )}
      <button onClick={onOpen}>View full details</button>
    </section>
  );
}
